import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ConfirmProvider } from "@/components/Confirm";
import { TenantProvider } from "@/context/TenantContext";
import { SettingsProvider } from "@/context/SettingsContext";
import {
  AdminRoute,
  ModuleRoute,
  OwnerRoute,
  ProtectedRoute,
  SignedInRoute,
} from "@/components/Bits";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import SetPassword from "@/pages/SetPassword";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import Inventory from "@/pages/Inventory";
import Purchases from "@/pages/Purchases";
import Sales from "@/pages/Sales";
import Expenses from "@/pages/Expenses";
import GiftBoxes from "@/pages/GiftBoxes";
import Reorder from "@/pages/Reorder";
import Categories from "@/pages/Categories";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";
import Profile from "@/pages/Profile";
import MyTickets from "@/pages/MyTickets";
import Landing from "@/pages/Landing";

// Split out: heavy pages, and ones most visits never reach. A first load
// then fetches only what the requested page needs.
const Clients = lazy(() => import("@/pages/Clients"));
const Overview = lazy(() => import("@/pages/Overview"));
const Platform = lazy(() => import("@/pages/Platform"));
const Support = lazy(() => import("@/pages/Support"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Shop = lazy(() => import("@/pages/Shop"));
const Valuation = lazy(() => import("@/pages/Valuation"));
const Customers = lazy(() => import("@/pages/Customers"));
const Invoices = lazy(() => import("@/pages/Invoices"));

export default function App() {
  return (
    <BrowserRouter>
      <ConfirmProvider>
      <AuthProvider>
        <TenantProvider>
          <SettingsProvider>
            <Suspense fallback={<div className="min-h-screen bg-surface" />}>
              <Routes>
              {/* Public — customers, no sign-in. */}
              <Route path="/shop/:slug" element={<Shop />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/set-password" element={<SetPassword />} />

              {/* Platform owner only */}
              <Route element={<OwnerRoute />}>
                <Route path="/overview" element={<Overview />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/platform" element={<Platform />} />
                <Route path="/support-inbox" element={<Support />} />
              </Route>

              {/* Inside a client */}
              <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/purchases" element={<Purchases />} />
                <Route path="/sales" element={<Sales />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/expenses" element={<Expenses />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/support" element={<MyTickets />} />
              </Route>

              {/* Optional features, hidden when a client switches them off */}
              <Route element={<ModuleRoute module="gift_boxes" />}>
                <Route path="/gift-boxes" element={<GiftBoxes />} />
              </Route>
              <Route element={<ModuleRoute module="reorder" />}>
                <Route path="/reorder" element={<Reorder />} />
              </Route>
              <Route element={<ModuleRoute module="valuation" />}>
                <Route path="/valuation" element={<Valuation />} />
              </Route>

              {/* Anyone signed in, client selected or not */}
              <Route element={<SignedInRoute />}>
                <Route path="/profile" element={<Profile />} />
              </Route>

              <Route element={<AdminRoute />}>
                <Route path="/settings" element={<Settings />} />
                <Route path="/team" element={<Team />} />
              </Route>

              <Route path="/" element={<Landing />} />
              <Route path="*" element={<Landing />} />
            </Routes>
            </Suspense>
            {/*
              Top right, but brief — it does sit over the page's main action, so
              it needs to clear itself quickly rather than waiting to be closed.
            */}
            <Toaster position="top-right" richColors closeButton duration={2000} />
          </SettingsProvider>
        </TenantProvider>
      </AuthProvider>
      </ConfirmProvider>
    </BrowserRouter>
  );
}
